import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCategoryManifest,
  getCategoryProducts,
} from "../../services/catalogService";
import { trackEvent } from "../../services/analyticsService";
import SafeImage from "../common/SafeImage";
import "../../styles/CategoryProductsPage.css";

/** UI page size (cards shown at once). Catalog shards remain 1000 products/page. */
const UI_PAGE_SIZE = 20;

const CategoryProductsPage = () => {
  const { subcategoryId } = useParams();
  const navigate = useNavigate();
  const [catalogPageData, setCatalogPageData] = useState(null);
  const [uiPage, setUiPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const trackedCategoryRef = useRef(null);

  useEffect(() => {
    setUiPage(1);
    trackedCategoryRef.current = null;
  }, [subcategoryId]);

  useEffect(() => {
    let cancelled = false;

    const loadCategoryPage = async () => {
      if (!subcategoryId) {
        setError("No subcategory ID provided. Unable to filter products.");
        setCatalogPageData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const category = await getCategoryManifest(subcategoryId);
        if (cancelled) return;

        if (!category) {
          setCatalogPageData(null);
          setError("No products found for the selected subcategory.");
          setLoading(false);
          return;
        }

        const catalogPageSize = Number(category.pageSize) || 1000;
        const productCount = Number(category.productCount) || 0;
        const totalUiPages = Math.max(
          1,
          Math.ceil(productCount / UI_PAGE_SIZE),
        );
        const safeUiPage = Math.min(Math.max(uiPage, 1), totalUiPages);
        const neededCatalogPage =
          productCount === 0
            ? 1
            : Math.floor(((safeUiPage - 1) * UI_PAGE_SIZE) / catalogPageSize) +
              1;

        const result = await getCategoryProducts(
          subcategoryId,
          neededCatalogPage,
        );
        if (cancelled) return;

        if (!result) {
          setCatalogPageData(null);
          setError("No products found for the selected subcategory.");
          setLoading(false);
          return;
        }

        setCatalogPageData(result);

        if (
          result.productCount > 0 &&
          trackedCategoryRef.current !== subcategoryId
        ) {
          trackedCategoryRef.current = subcategoryId;
          trackEvent("view_product_category", {
            category_id: subcategoryId,
          });
        }

        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading category products:", err);
        setError("Failed to load products. Please try again later.");
        setCatalogPageData(null);
        setLoading(false);
      }
    };

    loadCategoryPage();

    return () => {
      cancelled = true;
    };
  }, [subcategoryId, uiPage]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <h2>Loading products...</h2>
      </div>
    );
  }

  if (error) {
    return <h2>{error}</h2>;
  }

  const productCount = catalogPageData?.productCount || 0;
  const catalogPageSize = catalogPageData?.pageSize || 1000;
  const totalUiPages = Math.max(1, Math.ceil(productCount / UI_PAGE_SIZE));
  const safeUiPage = Math.min(Math.max(uiPage, 1), totalUiPages);

  if (!productCount) {
    return <h2>No products found.</h2>;
  }

  const offsetInCatalogPage =
    ((safeUiPage - 1) * UI_PAGE_SIZE) % catalogPageSize;
  const currentProducts = (catalogPageData?.products || []).slice(
    offsetInCatalogPage,
    offsetInCatalogPage + UI_PAGE_SIZE,
  );

  const handleNext = () => {
    if (safeUiPage < totalUiPages) setUiPage(safeUiPage + 1);
  };

  const handlePrevious = () => {
    if (safeUiPage > 1) setUiPage(safeUiPage - 1);
  };

  return (
    <div className="category-products-page">
      <h1>Products</h1>
      <div className="products-grid">
        {currentProducts.map((product, index) => (
          <div
            key={product.id || index}
            className="product-tile"
            onClick={() => {
              if (product.productNumber) {
                navigate(`/products/${product.productNumber}`, {
                  state: product,
                });
              }
            }}
            style={{ cursor: product.productNumber ? "pointer" : "default" }}
          >
            <SafeImage
              src={product.smallImagePath}
              alt={product.productTitle || "Product"}
            />
            <h3>{product.productTitle || "Untitled product"}</h3>
            <p>Part Number: {product.productNumber}</p>
            <p>Brand: {product.brand}</p>
          </div>
        ))}
      </div>

      <div className="pagination-controls">
        <button
          onClick={handlePrevious}
          disabled={safeUiPage === 1}
          className="pagination-button"
        >
          Previous
        </button>
        <span>
          Page {safeUiPage} of {totalUiPages}
        </span>
        <button
          onClick={handleNext}
          disabled={safeUiPage === totalUiPages}
          className="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CategoryProductsPage;
